import { memo, type ReactNode, useEffect } from 'react';

import { cn } from '@air/utils-tailwind';

import { useWorkspaceHeaderSearchLayout } from '~/components/v2/components/workspace/workspace-header/useWorkspaceHeaderSearchLayout';
import { WorkspaceHeaderInformation } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformation';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';

export type WorkspaceHeaderProps = {
  actions?: ReactNode;
  search?: ReactNode;
};

export const WorkspaceHeader = memo(({ actions, search }: WorkspaceHeaderProps) => {
  const rightPanelOpen = useWorkspaceRightPanelOpen();
  const setRightPanelOpen = useWorkspaceRightPanelStore((state) => state.setOpen);
  const selectedCanvasId = useWorkspaceRightPanelStore((state) => state.selectedCanvasId);
  const draftCanvas = useWorkspaceRightPanelStore((state) => state.draftCanvas);

  const { actionsRef, headerRef, layout, searchSlotRef, workspaceInfoRef } = useWorkspaceHeaderSearchLayout();
  const isSearchCentered = layout.mode === 'centered';

  useEffect(() => {
    if (!selectedCanvasId && !draftCanvas && rightPanelOpen) {
      setRightPanelOpen(false);
    }
  }, [rightPanelOpen, selectedCanvasId, draftCanvas, setRightPanelOpen]);

  return (
    <header
      className={cn(
        'h-16 ui2026:h-14 w-full shrink-0 items-center ads:border-default/80 border-gray-4/80 ui2026:border-b ads:bg-base ui2026:bg-gray-1',
        isSearchCentered ? 'grid' : 'flex',
      )}
      data-testid="WORKSPACE_CHROME_HEADER"
      data-workspace-chrome-header
      ref={headerRef}
      style={
        layout.mode === 'centered'
          ? { gridTemplateColumns: `${layout.gutterPx}px minmax(0, 1fr) ${layout.gutterPx}px` }
          : undefined
      }
    >
      {/*
        `w-max` only while centered so the measured side stays the content width, not the gutter.
        ui2026 workspace info already shrink-wraps (with a chevron-sized reserve) so hover cannot shift search.
      */}
      <div
        className={cn(
          'flex ui2026:h-full ui2026:w-max min-w-min shrink-0 justify-start',
          isSearchCentered && 'w-max justify-self-start',
        )}
        data-testid="WORKSPACE_HEADER_WORKSPACE_INFO"
        ref={workspaceInfoRef}
      >
        <WorkspaceHeaderInformation />
      </div>
      <div
        className="flex min-w-0 flex-1 items-center justify-center px-2"
        data-testid="WORKSPACE_HEADER_SEARCH_SLOT"
        ref={searchSlotRef}
      >
        {/* Keep in sync with WORKSPACE_HEADER_SEARCH_MAX_WIDTH_PX (product cap, not a spacing token). */}
        <div className="w-full min-w-0 max-w-[600px]">{search}</div>
      </div>
      <div
        className={cn('flex min-w-fit shrink-0 justify-end', isSearchCentered && 'w-max justify-self-end')}
        data-testid="WORKSPACE_HEADER_ACTIONS"
        ref={actionsRef}
      >
        {/* Padding on this row counts as side width, so leftover (and the cap) stay on search. */}
        <div className="flex gap-2 px-4">{actions}</div>
      </div>
    </header>
  );
});

WorkspaceHeader.displayName = 'WorkspaceHeader';
